import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import AuthContext from '../contexts/AuthContext'
import ScopeContext from '../contexts/ScopeContext'
import {
  getWeekOutcome,
  wasAnimationShown,
  markAnimationShown,
  formatWeekForDisplay
} from '../utilities/weekOutcome'

/**
 * Hook that checks for decided week outcomes and manages animations
 * 
 * Logic:
 * 1. Only works in group scope with 2+ members
 * 2. Only checks the last 2 weeks (current + previous) to avoid historical spam
 * 3. Finds weeks with decided outcomes (win/loss) that haven't been shown
 * 4. Returns the most recent such week for animation
 * 5. After animation completes, marks it as shown
 */
const useWeekResultAnimation = () => {
  const { user } = useContext(AuthContext)
  const { scores, currentScope, isGroupScope, scopeMembers } = useContext(ScopeContext)
  const [pendingAnimation, setPendingAnimation] = useState(null)
  
  // Track the last processed scores to avoid re-checking on every render
  const lastProcessedRef = useRef(null)

  useEffect(() => {
    // Only process in group scope with 2+ members
    if (!isGroupScope || !scores || scores.length === 0 || scopeMembers.length < 2 || !user) {
      setPendingAnimation(null)
      return
    }

    const groupId = currentScope?.group?.id
    if (!groupId) return

    // Create a signature to detect if we've already processed this data
    const scoresSignature = JSON.stringify({
      groupId,
      userId: user.id,
      weekCount: scores.length,
      // Include a hash of recent scores data to detect changes
      recentData: scores.slice(-2).map(w => ({
        start: w.start_of_week,
        data: Object.keys(w.data).sort().map(d => 
          Object.entries(w.data[d] || {}).sort().join(',')
        ).join('|')
      }))
    })

    // Skip if we've already processed this exact data
    if (lastProcessedRef.current === scoresSignature) {
      return
    }
    lastProcessedRef.current = scoresSignature

    const groupCreatedAt = scores[0]?.group_created_at || null
    const usernames = scopeMembers.map(m => m.username)

    // Only check the last 2 weeks (current + previous)
    // This handles the Monday login scenario where Sunday's result needs to be shown
    const weeksToCheck = scores.slice(-2)
    
    // Check recent weeks, collect those with unseen decided outcomes
    const decidedWeeks = []

    for (const weekData of weeksToCheck) {
      const weekStart = weekData.start_of_week

      // Skip if animation was already shown for this week
      if (wasAnimationShown(user.id, groupId, weekStart)) {
        continue
      }

      const outcome = getWeekOutcome(weekData, user.username, usernames, groupCreatedAt)

      if (outcome === 'win' || outcome === 'loss') {
        const weekIndex = scores.findIndex(w => w.start_of_week === weekStart)
        decidedWeeks.push({
          result: outcome,
          weekNumber: weekIndex + 1,
          weekStart,
          weekDisplay: formatWeekForDisplay(weekStart),
          groupId
        })
      }
    }

    // If there are decided weeks, show the most recent one
    if (decidedWeeks.length > 0) {
      // Sort by weekStart date descending to get most recent
      decidedWeeks.sort((a, b) => b.weekStart.localeCompare(a.weekStart))
      setPendingAnimation(decidedWeeks[0])
    } else {
      setPendingAnimation(null)
    }
  }, [scores, currentScope, isGroupScope, scopeMembers, user])

  const completeAnimation = useCallback(() => {
    if (pendingAnimation && user) {
      markAnimationShown(user.id, pendingAnimation.groupId, pendingAnimation.weekStart)
      setPendingAnimation(null)
      // Reset the processed ref to allow checking for additional animations
      lastProcessedRef.current = null
    }
  }, [pendingAnimation, user])

  return {
    pendingAnimation,
    completeAnimation
  }
}

export default useWeekResultAnimation